import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";
import { Alert } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { GridWrapper } from "../../../components";
import { CurrencyInput, DateInput, FormCheck, FormSelect, FormTextArea } from "../../../components/forms";
import { useInstruction } from "../contexts/InstructionContext";
import useIncome, { incomeFrequencyOptions } from "../hooks/useIncome";

const Income = ({
    taxFreeCash = false,
    clientWithdrawal = false,
    incomeCommencementDate = false,
    earmarkCash = true, //! Always true for now when Income is an option 
    inDrawdown = false,
    cashAccount = false,
    retainAmount = false,
    showRemainder = false,
    trusteeCashAccount = false
}) => {
    const [, { realTimeInstruction, invest, investIsLoading, investTransactionsIsLoading, divestIsLoading, divestTransactionsIsLoading }, { realTimeRemainder }] = useInstruction();
    const {
        patchInvest,
        realTimePatchInvestSingle,
        showTransferWarning
    } = useIncome();

    const isLoading = useMemo(() =>
        investIsLoading || investTransactionsIsLoading || divestIsLoading || divestTransactionsIsLoading,
        [divestIsLoading, divestTransactionsIsLoading, investIsLoading, investTransactionsIsLoading]);

    const frequencyNotApplicable = useMemo(() =>
        (realTimeInstruction?.invest?.clientIncomeAmount ?? 0) === 0,
        [realTimeInstruction?.invest?.clientIncomeAmount])

    const frequencyOptionsWithNotApplicable = useMemo(() => {
        if (incomeFrequencyOptions == null)
            return null;

        return frequencyNotApplicable
            ? [{ label: "Not Applicable", value: -1 }, ...incomeFrequencyOptions]
            : incomeFrequencyOptions;
    }, [frequencyNotApplicable]);

    return <span className={"d-block mt-3"}>
        {showTransferWarning &&
            <GridWrapper gridTemplateColumns={"minmax(0, 60ch)"}>
                <Alert variant="warning" className="mb-3" >
                    <Alert.Heading>
                        <FontAwesomeIcon icon='exclamation-triangle' size="lg" />
                        <span className="ps-2">
                            Warning
                        </span>
                    </Alert.Heading>
                    <span>
                        You have entered values that exceed the proceeds raised above.<br />
                        Please ensure this is intentional and/or fix the values.
                    </span>
                </Alert>
            </GridWrapper>}

        <div className="row gy-2 gx-2">
            {inDrawdown && <span className="align-self-end mb-2 col-auto">
                {isLoading
                    ? <Skeleton />
                    : <FormCheck
                        label={"In Drawdown"}
                        isChecked={invest?.isInDrawdown}
                        onChange={(isChecked) => patchInvest("isInDrawdown", isChecked)}
                    />}
            </span>}

            {taxFreeCash && <span className="col-4 col-lg-3 col-xl-2">
                {isLoading
                    ? <Skeleton />
                    : <CurrencyInput
                        label={"Tax Free Cash:"}
                        value={invest?.taxFreeCashAmount}
                        onChange={(_, { floatValue }) => realTimePatchInvestSingle("taxFreeCashAmount", floatValue)}
                        onBlur={(taxFree) => patchInvest("taxFreeCashAmount", taxFree)}
                        allowNegative={false}
                    />}
            </span>}

            {clientWithdrawal && <span className="col-4 col-lg-3 col-xl-2">
                {isLoading
                    ? <Skeleton />
                    : <CurrencyInput
                        label={"Client Withdrawal:"}
                        value={invest?.clientWithdrawalAmount}
                        onChange={(_, { floatValue }) => realTimePatchInvestSingle("clientWithdrawalAmount", floatValue)}
                        onBlur={(withdrawal) => patchInvest("clientWithdrawalAmount", withdrawal)}
                        allowNegative={false}
                    />}
            </span>}

            {cashAccount && <span className="col-4 col-lg-3 col-xl-2">
                {isLoading
                    ? <Skeleton />
                    : <CurrencyInput
                        label={trusteeCashAccount ? "Trustee Bank Account:" : "Cash Account:"}
                        value={invest?.toCashAccount}
                        onChange={(_, { floatValue }) => realTimePatchInvestSingle("toCashAccount", floatValue)}
                        onBlur={(cashAcc) => patchInvest("toCashAccount", cashAcc)}
                        allowNegative={false}
                    />}
            </span>}

            {retainAmount && <span className="col-4 col-lg-3 col-xl-2">
                {isLoading
                    ? <Skeleton />
                    : <CurrencyInput
                        label={"Retain to Invest Elsewhere:"}
                        value={invest?.retainAmount}
                        onChange={(_, { floatValue }) => realTimePatchInvestSingle("retainAmount", floatValue)}
                        onBlur={(retain) => patchInvest("retainAmount", retain)}
                        allowNegative={false}
                    />}
            </span>}

            <span className="w-100" />
            
            <span className="col-4 col-lg-3 col-xl-2">
                {isLoading
                    ? <Skeleton />
                    : <CurrencyInput
                        label={"Gross Income Amount:"}
                        value={invest?.clientIncomeAmount}
                        onChange={(_, { floatValue }) => realTimePatchInvestSingle("clientIncomeAmount", floatValue)}
                        onBlur={(income) => patchInvest("clientIncomeAmount", income)}
                        allowNegative={false}
                    />}
            </span>

            <span className="col-4 col-lg-3 col-xl-2">
                {isLoading
                    ? <Skeleton />
                    : <FormSelect
                        label={"Frequency:"}
                        isDisabled={frequencyNotApplicable}
                        defaultValue={frequencyNotApplicable
                            ? -1
                            : invest?.clientIncomeFrequency}
                        onChange={(selection) => realTimePatchInvestSingle("clientIncomeFrequency", selection.value)}
                        onBlur={(selection) => patchInvest("clientIncomeFrequency", selection.value)}
                        options={frequencyNotApplicable ? frequencyOptionsWithNotApplicable : incomeFrequencyOptions}
                    />}
            </span>

            {incomeCommencementDate && <span className="col-4 col-lg-3 col-xl-2">
                {isLoading
                    ? <Skeleton />
                    : <DateInput
                        label={"Commencing:"}
                        value={invest?.incomeCommencementDate?.substring(0, 10) || null}
                        isClearable
                        onBlur={(date) => patchInvest("incomeCommencementDate", date)}
                    />}
            </span>}

            {earmarkCash && <span className="col-12 col-xl-6 row p-0 mt-2 mx-0 gx-2">
                <span className="col-4 col-lg-3 col-xl-4">
                    {isLoading
                        ? <Skeleton />
                        : <CurrencyInput
                            label={"Earmark Cash:"}
                            isDisabled={frequencyNotApplicable}
                            value={realTimeInstruction?.invest?.earmarkCashAmount}
                            onChange={(_, { floatValue }) => realTimePatchInvestSingle("earmarkCashAmount", floatValue)}
                            onBlur={(earmark) => patchInvest("earmarkCashAmount", earmark)}
                        />}
                </span>

                {realTimeInstruction?.invest?.earmarkCashAmount > 0 && <span className="col-8 col-lg-9 col-xl-8">
                    {isLoading
                        ? <Skeleton />
                        : <FormTextArea
                            label={"Earmark Comment:"}
                            isDisabled={(realTimeInstruction?.invest?.earmarkCashAmount ?? 0) <= 0}
                            value={invest?.earmarkCashComment}
                            onBlur={(newText) => patchInvest("earmarkCashComment", newText)}
                        />}
                </span>}
            </span>}

            <span className="w-100" />

            {showRemainder && <span className="col-4 col-lg-3 col-xl-2">
                {isLoading
                    ? <Skeleton />
                    : <CurrencyInput
                        label={"Remaining in Cash:"}
                        value={realTimeRemainder}
                        disabled
                    />}
            </span>}
        </div>
    </span>;
}

export default Income;